import { yupResolver } from '@hookform/resolvers/yup'
import { AccessTimeFilled as AccessTimeFilledIcon, Check as CheckIcon } from '@mui/icons-material'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  SvgIcon,
  Tab,
  Tabs,
} from '@mui/material'
import Logo from '@src/assets/loop.svg?react'
import {
  useDownloadCreditPdf,
  useUpdateCreditApplication,
} from '@src/data/api/credit-application/credit-application-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import {
  useBusinessErrors,
  useFeatureFlags,
  useFinancingConfig,
  useResetErrors,
  useSetBusinessErrors,
} from '@src/data/selectors/app-config-selectors'
import { useIsRefinancing } from '@src/data/selectors/credit-selectors'
import {
  Applicant,
  CreditApplication,
  CreditApplicationSchemaContext,
  DraftApplicantSchema,
  Merchant,
  SelectValueListItem,
  buildDraftCreditApplicationSchema,
  buildFullCreditApplicationSchema,
  useDevTools,
} from '@src/data/types'
import { usePreferences } from '@src/providers/PreferencesProvider'
import validateCreditApplicationCanBeSubmittedForReview from '@src/services/credit-validations'
import { get } from 'lodash-es'
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { InputTextField, PageError, PageSpinner, SelectComponent, TabPanel } from '../../../../components'
import { Constants, InternalMerchants, isHardDeclineCreditApplication } from '../../../../data/types/constants'
import ApplicantParameters from './components/Applicant/applicantComponent'
import SaveDialog from './components/SaveDialog'
import TestCaseDialog from './ListTestCaseDialog'

import {
  EApplicantType,
  EBeneficiaryType,
  ECreditApplicationStatus,
  EFinancingProgram,
  EHomeStatus,
  ELoanPurpose,
  EProvince,
  ERelationToApplicant,
  getELoanPurposeList,
} from '@if/core/types'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import MerchantDialog from '../../../../components/SelectMerchantDialog'
import { reportErrorToConsole } from '../../../../services/error-logger'

interface Props {
  creditApplication: CreditApplication
  onSave: (data: Partial<CreditApplication>) => void
}

const CreditApplicationForm = ({ creditApplication, onSave }: Props) => {
  const SAVE_DIALOG = 'OpenSaveDialog'
  const PREQUAL_DIALOG = 'OpenPrequalDialog'
  const TEST_DIALOG = 'OpenTestDialog'
  const MERCHANT_DIALOG = 'OpenMerchantDialog'
  const [searchParams] = useSearchParams()
  const showTabIndex = searchParams.get('showTabIndex')
  const [hasCoapplicantChecked, setCoApplicantChecked] = useState<boolean>(!!creditApplication.coApplicant)
  const tabIndex = showTabIndex ? Number(showTabIndex) : 0
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')
  const [draftMod, setDraftMod] = useState(true)
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Track the current merchant ID - initialized from prop, updated when user selects a new merchant
  const [currentMerchantId, setCurrentMerchantId] = useState(creditApplication.merchantId)
  const [selectMerchant] = useGetMerchantById(currentMerchantId)
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const programFinancingConfig = useFinancingConfig(financingProgramId)
  const featureFlags = useFeatureFlags()
  const [merchantFinancingConfig, isFetchingMerchantConfig] = useMerchantFinancingConfig(
    selectMerchant?.id,
    financingProgramId,
  )
  const financingConfig = merchantFinancingConfig ? merchantFinancingConfig : programFinancingConfig

  const schemaContext: CreditApplicationSchemaContext = useMemo(
    () => ({
      financingConfig,
      merchantServiceCategory: selectMerchant?.serviceCategory ?? null,
    }),
    [financingConfig, selectMerchant?.serviceCategory],
  )

  const resolver = useMemo(() => {
    return (draftMod
      ? yupResolver(buildDraftCreditApplicationSchema(schemaContext))
      : yupResolver(buildFullCreditApplicationSchema(schemaContext))) as unknown as Resolver<
      CreditApplication,
      unknown,
      CreditApplication
    >
  }, [draftMod, schemaContext])

  const amountRequested = () => {
    if (creditApplication.initialRequestedLoanAmount == null) {
      return undefined
    }
    return Math.max(creditApplication.initialRequestedLoanAmount, creditApplication.requestedLoanAmount)
  }

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    control,
    getValues,
    setValue,
    formState: { errors },
    reset,
  } = useForm<CreditApplication>({
    mode: 'onBlur', // déclenche les validations Après que l'usager ait quitté le champ
    defaultValues: { ...creditApplication, requestedLoanAmount: amountRequested() },
    // Forcing type to be backward compatible with the old schema (should be removed in the future)
    resolver,
  })

  const [downloadCreditPdf] = useDownloadCreditPdf()

  const [, isSaving] = useUpdateCreditApplication()
  const error = useBusinessErrors()
  const setBusinessErrors = useSetBusinessErrors()
  const resetErrors = useResetErrors()
  const { selectedCompany } = usePreferences()
  const isRefinancing = useIsRefinancing(creditApplication)
  const editDisabled = creditApplication?.editLocked || isRefinancing
  const isHardDecline = useMemo(() => isHardDeclineCreditApplication(creditApplication), [creditApplication])
  const coApplicantToggleDisabled = editDisabled || isHardDecline

  //  Reset errors when component is mounted
  useEffect(() => {
    resetErrors()
  }, [resetErrors])

  //  Re-validate the max amount / validations
  useEffect(() => {
    const currentValue = watch('requestedLoanAmount')
    if (currentValue !== null && currentValue !== undefined) {
      void trigger('requestedLoanAmount')
    }
  }, [schemaContext, trigger, watch])

  const noCheckNeeded: boolean =
    creditApplication.financingProgramId === EFinancingProgram.Products &&
    watch('applicant.currentAddress.stateIso') !== EProvince.Quebec
  const loanPurposeId = watch('loanPurposeId') as ELoanPurpose
  const beneficiaryTypeId = Number(watch('beneficiaryTypeId')) as EBeneficiaryType
  const merchantId = watch('merchantId')
  const isVeterinaryLoanPurpose = ELoanPurpose.Veterinary === loanPurposeId
  const loanPurposesWithFixedBeneficiary = [ELoanPurpose.GoodsAndServices, ELoanPurpose.HomeSolutions]
  const shouldHideBeneficiarySelector = loanPurposesWithFixedBeneficiary.includes(loanPurposeId)
  const disableOtherName = beneficiaryTypeId !== EBeneficiaryType.Other
  const beneficiaryTypes = useMemo((): SelectValueListItem[] => {
    const beneficiaryTypesList: SelectValueListItem[] = []
    if (
      loanPurposeId &&
      (
        [
          ELoanPurpose.Aesthetics,
          ELoanPurpose.Medical,
          ELoanPurpose.Dentistry,
          ELoanPurpose.GoodsAndServices,
        ] as string[]
      ).includes(loanPurposeId, 0)
    ) {
      if (!beneficiaryTypeId) {
        setValue('beneficiaryTypeId', EBeneficiaryType.Applicant)
      }
      beneficiaryTypesList.push(
        {
          value: EBeneficiaryType.Applicant,
          label: t(`EBeneficiaryType.${EBeneficiaryType.Applicant}`, { ns: 'enum' }),
        },
        {
          value: EBeneficiaryType.Other,
          label: t(`EBeneficiaryType.${EBeneficiaryType.Other}`, { ns: 'enum' }),
        },
      )
      if (hasCoapplicantChecked) {
        beneficiaryTypesList.push({
          value: EBeneficiaryType.Coapplicant,
          label: t(`EBeneficiaryType.${EBeneficiaryType.Coapplicant}`, { ns: 'enum' }),
        })
      }
      if (ELoanPurpose.GoodsAndServices === loanPurposeId) {
        setValue('beneficiaryTypeId', EBeneficiaryType.Applicant)
        setValue('otherBeneficiaryFirstName', '')
        setValue('otherBeneficiaryLastName', '')
      }
    } else {
      beneficiaryTypesList.push({
        value: EBeneficiaryType.Other,
        label: t(`EBeneficiaryType.${EBeneficiaryType.Other}`, { ns: 'enum' }),
      })
      if (isVeterinaryLoanPurpose) {
        setValue('beneficiaryTypeId', EBeneficiaryType.Other)
        setValue('otherBeneficiaryFirstName', '')
      }
    }
    return beneficiaryTypesList
  }, [t, loanPurposeId, hasCoapplicantChecked, isVeterinaryLoanPurpose, setValue, beneficiaryTypeId])

  const loanPurposeOptions = useMemo(
    () =>
      getELoanPurposeList().map((item) => ({
        label: t(`ELoanPurpose.${item}`, { ns: 'enum' }),
        value: item,
      })),
    [t],
  )

  const isInternalMerchant = InternalMerchants.includes(merchantId)

  const handleTabChange = useCallback(
    (_event: SyntheticEvent, newValue: number) => {
      const { location } = window
      const updateSearch = new URLSearchParams(location.search)
      updateSearch.set('showTabIndex', encodeURIComponent(newValue))
      const newUrl = `${location.pathname}?${updateSearch.toString()}`
      navigate(newUrl, { replace: true })
    },
    [navigate],
  )

  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [setCurrentOpenDialog])

  const handleToggleCoapplicant = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setValue('coApplicant', DraftApplicantSchema().getDefault() as unknown as CreditApplication['coApplicant'])
        setValue('coApplicant.isPrimaryApplicant', false)
      } else {
        setValue('coApplicant', null)
      }
      setCoApplicantChecked(event.target.checked)
    },
    [setValue, setCoApplicantChecked],
  )

  const handleMerchantChanged = useCallback(
    (newMerchant: Merchant) => {
      // Update both form state and local state for merchant ID
      setValue('merchantId', newMerchant.id)
      setValue('loanPurposeId', newMerchant.serviceCategory ?? ('' as ELoanPurpose))
      setCurrentMerchantId(newMerchant.id)
      closeDialogs()
    },
    [closeDialogs, setValue],
  )

  const submit = useCallback(() => {
    closeDialogs()
    const data = getValues()
    data.status = draftMod ? ECreditApplicationStatus.Draft : ECreditApplicationStatus.Active
    data.consentSoftHit = !draftMod
    data.consentHardHit = !draftMod
    onSave(data)
  }, [onSave, getValues, draftMod, closeDialogs])

  const onFormSubmitted = useCallback(
    (data: CreditApplication) => {
      if (!draftMod) {
        const globalErrors = validateCreditApplicationCanBeSubmittedForReview(data)
        if (globalErrors.length > 0) {
          setBusinessErrors(globalErrors)
        } else {
          if (data.financingProgramId === EFinancingProgram.Auto) {
            downloadCreditPdf(data).catch(reportErrorToConsole)
          }
          setCurrentOpenDialog(SAVE_DIALOG)
        }
      } else {
        submit()
      }
    },
    [draftMod, submit, setCurrentOpenDialog, downloadCreditPdf, setBusinessErrors],
  )

  const onTestSelected = useCallback(
    (applicant: Applicant) => {
      const months = (applicant.currentAddress.months ?? 0) % 12
      applicant.currentAddress.years = ((applicant.currentAddress.months ?? 0) - months) / 12
      if (applicant.currentAddress.years === 0) {
        applicant.currentAddress.years = null
      }
      if (months === 0) {
        applicant.currentAddress.months = null
      } else {
        applicant.currentAddress.months = months
      }
      if (!applicant.expenses) {
        applicant.expenses = DraftApplicantSchema().getDefault().expenses as Applicant['expenses']
      }
      applicant.expenses!.homeFeeTypeId = EHomeStatus.Renter
      applicant.expenses!.housingMonthly = 1000

      if (tabIndex === 0) {        
        applicant.isPrimaryApplicant = true
        reset({ ...getValues(), applicant: applicant as CreditApplication['applicant'] })
      } else {
        applicant.isPrimaryApplicant = false
        applicant.relationWithApplicant = ERelationToApplicant.CommonLaw
        reset({ ...getValues(), coApplicant: applicant as CreditApplication['coApplicant'] })
      }
      closeDialogs()
    },
    [tabIndex, closeDialogs, reset, getValues],
  )

  const handlePrequalify = async () => {
    const isValid = await trigger()
    if (!isValid) {
      closeDialogs()
      return
    }

    const data = getValues()
    const globalErrors = validateCreditApplicationCanBeSubmittedForReview(data)
    if (globalErrors.length > 0) {
      setBusinessErrors(globalErrors)
    } else {
      data.status = ECreditApplicationStatus.Active
      data.consentSoftHit = true
      data.consentHardHit = false
      onSave(data)
    }
    closeDialogs()
  }

  const financingProgramConfig = get(
    Constants.financingProgramConfigs,
    financingProgramId?.toLowerCase() ?? 'default',
    Constants.financingProgramConfigs.default,
  )

  return (
    <div style={{ height: '100%' }}>
      <SaveDialog
        onClose={closeDialogs}
        open={currentOpenDialog === SAVE_DIALOG}
        onSubmit={submit}
        noCheckNeeded={noCheckNeeded}
      />
      <Dialog onClose={closeDialogs} open={currentOpenDialog === PREQUAL_DIALOG} onSubmit={handlePrequalify}>
        <DialogContent>{t('common.prequalify')}?</DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeDialogs}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handlePrequalify}>
            {t('common.prequalify')}
          </Button>
        </DialogActions>
      </Dialog>
      <PageSpinner isLoading={isSaving || isFetchingMerchantConfig} withBackdrop />
      <TestCaseDialog
        onClose={closeDialogs}
        open={currentOpenDialog === TEST_DIALOG}
        financingProgramId={watch('financingProgramId')}
        onTestCaseSelected={onTestSelected}
      />
      <Paper>
        <PageError errors={error} />
        <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
          <Grid container mb={4}>
            <Grid display="flex" justifyContent="flex-end" size={12}>
              <div>
                {creditApplication.id && (
                  <>
                    <Chip
                      sx={{ marginRight: '1rem' }}
                      label={t('common.consentSoftHit')}
                      icon={
                        creditApplication?.consentSoftHit ? (
                          <CheckIcon color="success" />
                        ) : (
                          <AccessTimeFilledIcon color="warning" />
                        )
                      }
                    />
                    <Chip
                      sx={{ marginRight: '1rem' }}
                      label={t('common.consentHardHit')}
                      icon={
                        creditApplication?.consentHardHit ? (
                          <CheckIcon color="success" />
                        ) : (
                          <AccessTimeFilledIcon color="warning" />
                        )
                      }
                    />
                  </>
                )}
                {creditApplication.status === ECreditApplicationStatus.Draft && (
                  <Button
                    sx={{ marginRight: 2 }}
                    type="submit"
                    variant="contained"
                    value="draft"
                    color="secondary"
                    disabled={isSaving}
                    onClick={() => setDraftMod(true)}
                  >
                    {t('common.saveDraft')}
                  </Button>
                )}
                {financingProgramConfig.useSoftHit && (
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isSaving || editDisabled}
                    onClick={() => {
                      setDraftMod(false)
                      setCurrentOpenDialog(PREQUAL_DIALOG)
                    }}
                  >
                    {t('common.prequalify')}
                  </Button>
                )}
                <Button
                  sx={{ marginLeft: 2 }}
                  type="submit"
                  variant="contained"
                  color="warning"
                  value="finale"
                  disabled={isSaving || editDisabled}
                  onClick={() => setDraftMod(false)}
                >
                  {t('common.submitAndPrint')}
                </Button>
              </div>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              {!creditApplication.id && (
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: (
                        <IconButton aria-label="swap" onClick={() => setCurrentOpenDialog(MERCHANT_DIALOG)}>
                          <SvgIcon fontSize="large">
                            <Logo />
                          </SvgIcon>
                        </IconButton>
                      ),
                    },
                  }}
                  value={selectMerchant?.name ?? ''}
                  disabled
                  label={t('common.merchant')}
                  error={errors?.merchantId}
                />
              )}
            </Grid>

            {financingProgramConfig.requestedLoanAmountIsRequired && (
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: '$',
                    },
                  }}
                  disabled={editDisabled}
                  error={errors?.requestedLoanAmount}
                  label={t('editCreditApplication.amountRequested')}
                  {...register('requestedLoanAmount')}
                />
              </Grid>
            )}
            {(creditApplication.financingProgramId === EFinancingProgram.AutoRsla ||
              creditApplication.financingProgramId === EFinancingProgram.CreditRsla) && (
              <Grid
                size={{
                  xs: 12,
                  md: 3,
                }}
              >
                <InputTextField
                  disabled={editDisabled}
                  error={errors?.brokerReferenceNumber}
                  label={t('editCreditApplication.brokerReferenceNumber')}
                  {...register('brokerReferenceNumber')}
                />
              </Grid>
            )}
            {creditApplication.financingProgramId === EFinancingProgram.Personal && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <SelectComponent
                    items={loanPurposeOptions}
                    label={t('editCreditApplication.loanPurpose')}
                    {...register('loanPurposeId')}
                    error={errors?.loanPurposeId}
                    disabled={editDisabled || !isInternalMerchant}
                  />
                </Grid>
                {!shouldHideBeneficiarySelector && (
                  <Grid container spacing={2} margin={0}>
                    <Grid
                      size={{
                        xs: 4,
                        md: 4,
                      }}
                    >
                      <SelectComponent
                        items={beneficiaryTypes}
                        label={t('editCreditApplication.beneficiary.beneficiary')}
                        disabled={isVeterinaryLoanPurpose || editDisabled}
                        {...register('beneficiaryTypeId')}
                      />
                    </Grid>
                    {!isVeterinaryLoanPurpose && (
                      <Grid
                        size={{
                          xs: 4,
                          md: 4,
                        }}
                      >
                        <InputTextField
                          error={errors?.otherBeneficiaryFirstName}
                          label={t('editCreditApplication.beneficiary.otherBeneficiaryFirstName')}
                          disabled={disableOtherName || editDisabled}
                          {...register('otherBeneficiaryFirstName')}
                          maxLength={50}
                        />
                      </Grid>
                    )}
                    <Grid
                      size={{
                        xs: isVeterinaryLoanPurpose ? 8 : 4,
                        md: isVeterinaryLoanPurpose ? 8 : 4,
                      }}
                    >
                      <InputTextField
                        error={errors?.otherBeneficiaryLastName}
                        label={
                          !isVeterinaryLoanPurpose
                            ? t('editCreditApplication.beneficiary.otherBeneficiaryLastName')
                            : t('editCreditApplication.beneficiary.animalName')
                        }
                        disabled={disableOtherName || editDisabled}
                        {...register('otherBeneficiaryLastName')}
                        maxLength={50}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
            {selectMerchant && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <InputTextField
                    error={errors?.merchantReferenceCode}
                    label={t('editCreditApplication.merchantReferenceCode')}
                    {...register('merchantReferenceCode', { maxLength: 20 })}
                    maxLength={20}
                  />
                </Grid>
              </>
            )}

            {featureFlags.usePersonalLoanServiceProviderName && selectMerchant && InternalMerchants.includes(selectMerchant.id) && (
              <>
                <Grid
                  size={{
                    xs: 12,
                    md: 3,
                  }}
                >
                  <InputTextField
                    error={errors?.unregisteredMerchantName}
                    label={t('editCreditApplication.unregisteredMerchantName.label')}
                    {...register('unregisteredMerchantName', { maxLength: 50 })}
                    maxLength={50}
                  />
                </Grid>
              </>
            )}
            {financingProgramConfig.supportsSinglePayment && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mt: 2, mb: 1 }}>
                    <FormControl component="fieldset" disabled={editDisabled}>
                      <FormLabel component="legend">{t('editCreditApplication.paymentOptions')}</FormLabel>
                      <RadioGroup
                        row
                        value={watch('isSinglePayment') === true ? 'single' : 'loan'}
                        onChange={(event) => {
                          const isSingle = event.target.value === 'single'
                          setValue('isSinglePayment', isSingle)
                        }}
                        sx={{ pl: 1 }}
                      >
                        <FormControlLabel
                          value="loan"
                          control={<Radio />}
                          label={t('editCreditApplication.financing')}
                          disabled={editDisabled}
                        />
                        <FormControlLabel
                          value="single"
                          control={<Radio />}
                          label={t('editCreditApplication.singlePayment')}
                          disabled={editDisabled}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs variant="fullWidth" value={tabIndex} onChange={handleTabChange} aria-label="basic tabs example">
              <Tab
                icon={errors.applicant ? <PriorityHighIcon color="error" /> : ''}
                iconPosition="end"
                label={t('common.applicant')}
              />
              {financingProgramConfig.supportsCoApplicant && (
                <Tab
                  icon={errors.coApplicant ? <PriorityHighIcon color="error" /> : ''}
                  iconPosition="end"
                  label={t('common.coApplicant')}
                />
              )}
            </Tabs>
          </Box>
          <Box sx={{ flex: '1 1 auto' }} />
          <input type="hidden" value=" " {...register('id')} />
          <input type="hidden" value=" " {...register('merchantId')} />
          <TabPanel value={tabIndex} index={0}>
            {useDevTools && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  variant="contained"
                  color="secondary"
                  onClick={() => setCurrentOpenDialog(TEST_DIALOG)}
                >
                  Cas de test
                </Button>
              </Box>
            )}
            <ApplicantParameters
              register={register}
              watch={watch}
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              errors={errors.applicant}
              applicantType={EApplicantType.Applicant}
              formControl={control}
              editDisabled={editDisabled}
              financingProgramId={creditApplication.financingProgramId}
              financingProgramConfig={financingProgramConfig}
            />
          </TabPanel>
          {financingProgramConfig.supportsCoApplicant && (
            <TabPanel value={tabIndex} index={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasCoapplicantChecked}
                    onChange={handleToggleCoapplicant}
                    disabled={coApplicantToggleDisabled}
                  />
                }
                label={t('common.coApplicant')}
              />
              {hasCoapplicantChecked && (
                <>
                  {useDevTools && (
                    <Box sx={{ display: 'flex', flexDirection: 'row', p: 2 }}>
                      <Button
                        type="button"
                        variant="contained"
                        color="secondary"
                        onClick={() => setCurrentOpenDialog(TEST_DIALOG)}
                      >
                        Cas de test
                      </Button>
                    </Box>
                  )}
                  <ApplicantParameters
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    getValues={getValues}
                    trigger={trigger}
                    errors={errors?.coApplicant}
                    applicantType={EApplicantType.CoApplicant}
                    formControl={control}
                    editDisabled={editDisabled}
                    financingProgramId={creditApplication.financingProgramId}
                    financingProgramConfig={financingProgramConfig}
                  />
                </>
              )}
            </TabPanel>
          )}
        </form>
      </Paper>
      <MerchantDialog
        open={currentOpenDialog === MERCHANT_DIALOG}
        title={t('worksheet.modifyMerchant')}
        label={t('taskManager.merchantName')}
        onConfirm={handleMerchantChanged}
        onCancel={closeDialogs}
        financingProgramId={creditApplication.financingProgramId}
        financingCompanyId={selectedCompany}
        allowMultiple={false}
      />
    </div>
  )
}

export default CreditApplicationForm
